import { useState } from 'react'
import { Button } from '#app/components/ui/button.tsx'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '#app/components/ui/card.tsx'
import { JournalEditor } from './journal-editor.tsx'
import { LedgerChart } from './ledger-chart.tsx'
import {
	formatMoney,
	net,
	PALETTE,
	type ChartSeries,
	type JournalLine,
	type Version,
} from './ledger.ts'
import { TimeTravelSlider } from './time-travel-slider.tsx'

const SEED: JournalLine[] = [
	{ id: 'i1', date: '2026-01-31', kind: 'revenue', amount: 1200 },
	{ id: 'i2', date: '2026-02-28', kind: 'expense', amount: 400 },
	{ id: 'i3', date: '2026-03-31', kind: 'revenue', amount: 900 },
]

export function ImmutableBook() {
	const [draft, setDraft] = useState<JournalLine[]>(SEED)
	const [versions, setVersions] = useState<Version[]>([])
	const [cursor, setCursor] = useState(1) // 1-based knowledge-time index

	function commit() {
		const label = `t${versions.length + 1}`
		setVersions([...versions, { label, lines: draft.map((l) => ({ ...l })) }])
		setCursor(versions.length + 1)
	}

	const series: ChartSeries[] = versions.map((v, i) => ({
		key: v.label,
		label: v.label,
		color: PALETTE[i % PALETTE.length] ?? '#6366f1',
		lines: v.lines,
	}))

	const current = versions[cursor - 1]

	return (
		<Card className="border-indigo-500/40">
			<CardHeader>
				<CardTitle className="flex items-center gap-2">
					<span className="inline-block h-2.5 w-2.5 rounded-full bg-indigo-500" />
					Immutable book
				</CardTitle>
				<CardDescription>
					Each <strong>commit</strong> appends to an immutable log — nothing is
					overwritten. To fix a mistake you{' '}
					<strong>append an adjusting line</strong>; the original entry and its
					correction both stay on the record, and every past state stays
					queryable, plotted as its own series.
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				<JournalEditor lines={draft} onChange={setDraft} />
				<Button type="button" onClick={commit} className="w-full">
					Commit (append to log · t{versions.length + 1})
				</Button>

				<LedgerChart series={series} highlightKey={current?.label} />

				{versions.length > 0 && (
					<>
						<TimeTravelSlider
							label="Time-travel: as of knowledge-time…"
							hint={
								current
									? `${current.label} · net ${formatMoney(net(current.lines))}`
									: ''
							}
							min={1}
							max={versions.length}
							value={cursor}
							onChange={setCursor}
						/>
						<p className="text-muted-foreground text-xs">
							Scrub to any point in knowledge-time — nothing was saved as a
							copy; each state is <em>derived</em> from the log on demand.
						</p>
					</>
				)}

				<p className="text-muted-foreground border-t pt-3 text-xs">
					<strong>Honest cost:</strong> the log only grows by what changed (
					{versions.length} entr{versions.length === 1 ? 'y' : 'ies'}), never by
					full copies. At scale you materialise the current state for speed and
					answer the past with as-of queries — but you never lose it.
				</p>
			</CardContent>
		</Card>
	)
}
